'use client';

import type { Key } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Badge } from '@savvy-kit/components/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@savvy-kit/components/dropdown-menu';
import Tag from '@savvy-kit/patterns/tag';
import { isNil } from 'es-toolkit';
import { ChevronDown } from 'lucide-react';

type FilterOption<T extends Key> = {
  readonly value: T;
  readonly label: string;
  readonly color?: string;
};

type ActiveChecklistFilterDropdownProps<T extends Key> = {
  readonly visibleOptions: readonly FilterOption<T>[];
  readonly remainingCount: number;
  readonly hasMoreOptions: boolean;
  readonly onRemoveOption: (value: T) => void;
  readonly filterLabel: string;
  readonly filterContent: React.ReactNode;
};

export function ActiveChecklistFilterDropdown<T extends Key>({
  visibleOptions,
  remainingCount,
  hasMoreOptions,
  onRemoveOption,
  filterLabel,
  filterContent,
}: ActiveChecklistFilterDropdownProps<T>) {
  const [dropdownWidth, setDropdownWidth] = useState<number | undefined>(undefined);
  const [alignOffset, setAlignOffset] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (containerRef.current && triggerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const triggerRect = triggerRef.current.getBoundingClientRect();
      setDropdownWidth(containerRect.width);
      setAlignOffset(containerRect.left - triggerRect.left);
    }
  }, [hasMoreOptions, visibleOptions.length]);

  return (
    <div
      ref={containerRef}
      className="flex items-center flex-row flex-wrap border border-border-light rounded-md gap-2 px-2 py-2"
    >
      <span className="text-dark font-medium normal-case text-sm not-italic border-r border-border-light pr-2 mr-2">
        {filterLabel}
      </span>
      {visibleOptions.map((option) => (
        <Tag
          key={option.value}
          {...(!isNil(option.color) ? { color: option.color } : {})}
          type="filter"
          dismissible
          onDismiss={() => {
            onRemoveOption(option.value);
          }}
        >
          {option.label}
        </Tag>
      ))}
      {hasMoreOptions && (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
          <DropdownMenuTrigger
            render={
              <button
                ref={triggerRef}
                type="button"
                className="flex items-center gap-1 cursor-pointer border-none bg-transparent p-0 hover:opacity-70"
                aria-label="Show more options"
              />
            }
          >
            <Badge variant="outline" className="shrink-0">
              +{remainingCount}
            </Badge>
            <ChevronDown className="size-4 text-dark shrink-0" />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="p-0 overflow-x-hidden"
            align="start"
            sideOffset={10}
            alignOffset={alignOffset}
            style={!isNil(dropdownWidth) ? { width: dropdownWidth } : undefined}
          >
            {filterContent}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
